/**
 * Security → Devices & sign-ins and Blocked countries.
 *
 * Every browser sign-in and connected app (REWS for Android, ContactMapper)
 * is a session. An app that redeems a connect code waits for approval
 * unless its device is set to Always allow; a banned device can't connect
 * again until it's unbanned.
 */
import { api } from '@/shared/api';

export type SessionKind = 'browser' | 'app';
export type SessionStatus = 'pending' | 'active' | 'revoked';
export type DeviceAccess = 'ask' | 'always';

export interface AuthSessionRecord {
    id: number;
    kind: SessionKind;
    client: string;
    client_label: string;
    status: SessionStatus;
    device_id: string | null;
    device_name: string | null;
    model: string | null;
    platform: string | null;
    os_version: string | null;
    app_version: string | null;
    browser: string | null;
    os: string | null;
    created_at: string;
    last_seen_at: string;
    created_ip: string | null;
    last_ip: string | null;
    /** ISO 3166-1 alpha-2, or null when the location couldn't be told. */
    country: string | null;
    new_location: boolean;
    /** Signed in before sessions were tracked (no device details). */
    older_sign_in: boolean;
    is_current: boolean;
    device_access: DeviceAccess | null;
}

export interface BannedDeviceRecord {
    device_id: string;
    device_name: string | null;
    model: string | null;
    platform: string | null;
    banned_at: string | null;
    banned_reason: string | null;
}

export interface SessionsPayload {
    sessions: AuthSessionRecord[];
    banned_devices: BannedDeviceRecord[];
}

export interface BlockedCountryRecord {
    code: string;
    name: string;
    note: string | null;
    created_at: string | null;
}

export interface BlockedCountriesPayload {
    countries: BlockedCountryRecord[];
    /** Where this browser is signed in from (it can't be blocked). */
    current_country: string | null;
}

export interface ChangePasswordInput {
    current_password: string;
    new_password: string;
    sign_out_others: boolean;
    disconnect_apps: boolean;
}

export const sessionsApi = {
    list: (): Promise<SessionsPayload> => api.get<SessionsPayload>('/auth/sessions'),

    approve: (id: number, always: boolean): Promise<unknown> =>
        api.post(`/auth/sessions/${id}/approve`, { always }),

    disconnect: (id: number): Promise<unknown> => api.post(`/auth/sessions/${id}/disconnect`),

    signOutOthers: (disconnectApps: boolean): Promise<{ signed_out: number }> =>
        api.post<{ signed_out: number }>('/auth/sessions/sign-out-others', { disconnect_apps: disconnectApps }),

    ban: (deviceId: string, reason?: string): Promise<unknown> =>
        api.post(`/auth/devices/${encodeURIComponent(deviceId)}/ban`, { reason: reason || null }),

    unban: (deviceId: string): Promise<unknown> => api.post(`/auth/devices/${encodeURIComponent(deviceId)}/unban`),

    setAlwaysAllow: (deviceId: string, always: boolean): Promise<unknown> =>
        api.post(`/auth/devices/${encodeURIComponent(deviceId)}/access`, { always }),

    changePassword: (input: ChangePasswordInput): Promise<{ signed_out: number }> =>
        api.post<{ signed_out: number }>('/auth/change-password', input),

    blockedCountries: (): Promise<BlockedCountriesPayload> =>
        api.get<BlockedCountriesPayload>('/auth/blocked-countries'),

    blockCountry: (code: string, name: string): Promise<BlockedCountriesPayload> =>
        api.post<BlockedCountriesPayload>('/auth/blocked-countries', { code, name }),

    unblockCountry: (code: string): Promise<BlockedCountriesPayload> =>
        api.delete<BlockedCountriesPayload>(`/auth/blocked-countries/${code}`),
};
